import { useCallback, useEffect, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getStoredUser, parseJsonSafe } from '../api/client';
import { getSessionSummary } from '../api/tutor';
import { formatDateTime, formatMarks, formatSkill, formatState } from '../lib/format';

function aggregateWeakSkills(weakSkills) {
  const focusAreasByLabel = new Map();

  weakSkills.forEach((skill) => {
    const label = formatSkill(skill.display_name || skill.tag);
    const existing = focusAreasByLabel.get(label);

    if (existing) {
      existing.count += skill.count;
    } else {
      focusAreasByLabel.set(label, { label, count: skill.count });
    }
  });

  return Array.from(focusAreasByLabel.values());
}

function SessionSummaryPage() {
  const { sessionId: sessionIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateSummary = location?.state?.summary ?? location?.state?.sessionSummary ?? null;
  const stateSessionId = location?.state?.sessionId ?? null;
  const fromSessionPath = location?.state?.fromSessionPath ?? null;
  const effectiveSessionId = sessionIdParam || stateSessionId || '';
  const role = getStoredUser()?.role || null;
  const isParent = role === 'PARENT';
  const showTutorSignals = role === 'TUTOR' || role === 'ADMIN';
  const [summary, setSummary] = useState(stateSummary);
  const [isLoading, setIsLoading] = useState(!stateSummary);
  const [errorMessage, setErrorMessage] = useState('');

  const buildErrorMessage = useCallback(async (response, fallback) => {
    const status = response?.status;
    let detail = '';

    if (response) {
      const data = await parseJsonSafe(response);
      detail = data?.detail || data?.message || '';
    }

    if (status === 403) {
      return 'Not linked to this student/session.';
    }

    const detailSuffix = detail ? ` - ${detail}` : '';
    return `${fallback} (status ${status ?? 'unknown'})${detailSuffix}`;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSummary = async () => {
      if (!effectiveSessionId) {
        setErrorMessage('Missing session id for summary.');
        setIsLoading(false);
        return;
      }

      if (stateSummary) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await getSessionSummary(effectiveSessionId);

        if (!response.ok) {
          const message = await buildErrorMessage(
            response,
            'Failed to load session summary.'
          );
          throw new Error(message);
        }

        const data = await parseJsonSafe(response);
        if (isMounted) {
          setSummary(data ?? null);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'Failed to load session summary.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      isMounted = false;
    };
  }, [buildErrorMessage, effectiveSessionId, stateSummary]);

  const totals = summary?.totals ?? {};
  const weakSkills = summary?.weak_skills ?? [];
  const focusAreas = aggregateWeakSkills(weakSkills);
  const homeworkSuggestions = summary?.homework_suggestions ?? [];
  const totalScore = typeof totals.total_score === 'number' ? totals.total_score : 0;
  const totalMax = typeof totals.total_max === 'number' ? totals.total_max : 0;
  const fallbackPercent =
    totalMax > 0 ? Math.round((totalScore / totalMax) * 1000) / 10 : null;
  const accuracyPercent =
    typeof totals.accuracy_percent === 'number' ? totals.accuracy_percent : null;
  const marksMissed =
    typeof totals.marks_missed === 'number' ? totals.marks_missed : null;
  const partsOverridden =
    typeof totals.parts_overridden === 'number' ? totals.parts_overridden : 0;
  const partsSubmitted =
    typeof totals.parts_submitted === 'number' ? totals.parts_submitted : null;
  const partsTutorConfirmed =
    typeof totals.parts_tutor_confirmed === 'number' ? totals.parts_tutor_confirmed : 0;
  const partsTutorConfirmedLowConfidence =
    typeof totals.parts_tutor_confirmed_low_confidence === 'number'
      ? totals.parts_tutor_confirmed_low_confidence
      : 0;
  const partsTutorAdjustedAndConfirmed =
    typeof totals.parts_tutor_adjusted_and_confirmed === 'number'
      ? totals.parts_tutor_adjusted_and_confirmed
      : 0;
  const percent = accuracyPercent !== null ? accuracyPercent : fallbackPercent;
  const status = summary?.status ?? 'unknown';
  const isEnded = status === 'ENDED' || status === 'COMPLETED' || status === 'ABANDONED';
  const canReturnToSession = Boolean(summary) && !isEnded && !isParent;

  const suggestions = Array.isArray(homeworkSuggestions) ? homeworkSuggestions : [];
  const tutorSignals = showTutorSignals && summary?.tutor_signals ? summary.tutor_signals : null;
  const parentProgressSnapshot = summary?.parent_progress_snapshot ?? null;
  const practice = suggestions.filter(
    (s) => typeof s === 'string' && s.startsWith('Practice:')
  );
  const reviewDue = suggestions.filter(
    (s) => typeof s === 'string' && s.startsWith('Review due:')
  );
  const notes = suggestions.filter(
    (s) =>
      typeof s === 'string' &&
      !s.startsWith('Practice:') &&
      !s.startsWith('Review due:')
  );

  const stripPrefix = (value, prefix) =>
    typeof value === 'string' && value.startsWith(prefix)
      ? value.slice(prefix.length).trim()
      : value;

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Session wrap-up</p>
          <h1>Session Summary</h1>
          <p className="muted-text">A review of this practice session</p>
        </div>
        <div className="page-header-actions">
          {canReturnToSession ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() =>
                navigate(fromSessionPath || `/sessions/${effectiveSessionId}/tutor`)
              }
              disabled={!effectiveSessionId}
            >
              Return to session
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Back to dashboard
          </button>
        </div>
      </section>
      {isLoading ? <p>Loading summary...</p> : null}
      {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
      {!isLoading && !errorMessage && summary ? (
        <section className="session-summary-grid">
          <article className="card session-summary-outcome">
          <div className="section-header">
            <div>
              <p className="eyebrow">Overview</p>
              <h2>Outcome</h2>
            </div>
            <span className="status-pill">
              {formatState(status, 'general', { questionCountMode: summary?.question_count_mode })}
            </span>
          </div>
          {summary.ended_at ? <p className="session-summary-ended">Ended {formatDateTime(summary.ended_at)}</p> : null}
          {isParent && parentProgressSnapshot ? (
            <div className="stack-sm">
              <p className="section-title">Progress snapshot</p>
              <dl className="session-summary-detail-list">
                <div><dt>Current focus</dt><dd>{formatSkill(parentProgressSnapshot.current_focus)}</dd></div>
                <div><dt>Current challenge</dt><dd>{formatSkill(parentProgressSnapshot.current_challenge)}</dd></div>
                <div><dt>Next action</dt><dd>{parentProgressSnapshot.next_action}</dd></div>
              </dl>
            </div>
          ) : null}
          <div className="stack-sm">
            <p className="section-title">Totals</p>
            <dl className="session-summary-metrics">
              <div><dt>Accuracy</dt><dd>{percent === null ? 'N/A' : `${percent}%`}</dd></div>
              <div><dt>Total score</dt><dd>{formatMarks(totals.total_score ?? 0)} / {formatMarks(totals.total_max ?? 0)}</dd></div>
              <div><dt>Questions completed</dt><dd>{totals.questions_completed ?? 0}</dd></div>
              <div><dt>Questions attempted</dt><dd>{totals.questions_attempted ?? 0}</dd></div>
            </dl>
            <ul className="session-summary-supporting-list">
              <li>Questions assigned: {totals.questions_assigned ?? 0}</li>
              <li>Questions abandoned: {totals.questions_abandoned ?? 0}</li>
              <li>Parts marked: {totals.parts_marked ?? 0}</li>
              {partsSubmitted !== null ? (
                <li>Parts submitted: {partsSubmitted}</li>
              ) : null}
              {marksMissed !== null ? <li>Marks missed: {formatMarks(marksMissed)}</li> : null}
              {partsOverridden > 0 ? (
                <li>Tutor adjusted parts: {partsOverridden}</li>
              ) : null}
              {showTutorSignals && partsTutorConfirmed > 0 ? (
                <li>Tutor confirmed parts: {partsTutorConfirmed}</li>
              ) : null}
              {showTutorSignals && partsTutorConfirmedLowConfidence > 0 ? (
                <li>
                  Confirmed originally low-confidence parts: {partsTutorConfirmedLowConfidence}
                </li>
              ) : null}
              {showTutorSignals && partsTutorAdjustedAndConfirmed > 0 ? (
                <li>
                  Tutor adjusted and confirmed parts: {partsTutorAdjustedAndConfirmed}
                </li>
              ) : null}
            </ul>
          </div>
          </article>
          <article className="card session-summary-focus">
            {tutorSignals ? (
              <div className="stack-sm">
                <p className="section-title">Tutor signals</p>
                <ul>
                  <li>
                    Method security: {tutorSignals.method_security?.label || 'Not enough evidence'}
                    {tutorSignals.method_security?.detail
                      ? ` - ${tutorSignals.method_security.detail}`
                      : ''}
                  </li>
                  <li>
                    Scaffold dependence: {tutorSignals.scaffold_dependence?.label || 'No scaffold signal yet'}
                    {tutorSignals.scaffold_dependence?.detail
                      ? ` - ${tutorSignals.scaffold_dependence.detail}`
                      : ''}
                  </li>
                  <li>
                    Readiness: {tutorSignals.readiness_for_independent_exam_practice?.label || 'Not enough evidence'}
                    {tutorSignals.readiness_for_independent_exam_practice?.detail
                      ? ` - ${tutorSignals.readiness_for_independent_exam_practice.detail}`
                      : ''}
                  </li>
                </ul>
              </div>
            ) : null}
            <div className="stack-sm">
            <p className="section-title">Focus areas</p>
            <p>These skills appeared most often in low-confidence or low-score moments.</p>
            {focusAreas.length ? (
              <ul>
                {focusAreas.map((focusArea, index) => (
                  <li key={`focus-area-${index}-${focusArea.label}`}>
                    {focusArea.label} ({focusArea.count})
                  </li>
                ))}
              </ul>
            ) : (
              <p>No weak skills reported.</p>
            )}
            </div>
          </article>
          <article className="card session-summary-homework">
            <p className="section-title">Homework suggestions</p>
            {suggestions.length ? (
              <div>
                {practice.length ? (
                  <div>
                    <p>Homework: Practice</p>
                    <ul>
                      {practice.map((item, index) => (
                        <li key={`practice-${item}-${index}`}>
                          {stripPrefix(item, 'Practice:')}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {reviewDue.length ? (
                  <div>
                    <p>Homework: Review due</p>
                    <ul>
                      {reviewDue.map((item, index) => (
                        <li key={`review-due-${item}-${index}`}>
                          {stripPrefix(item, 'Review due:')}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {notes.length ? (
                  <div>
                    <p>Notes</p>
                    <ul>
                      {notes.map((item, index) => (
                        <li key={`notes-${item}-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
              <p>No homework suggestions yet — complete a few more questions.</p>
            )}
          </article>
        </section>
      ) : null}
      {!isLoading && !errorMessage && !summary ? (
        <p className="muted-text">No summary available yet.</p>
      ) : null}
    </div>
  );
}

export default SessionSummaryPage;
